import React, { useEffect, useRef, useState, memo } from 'react';
import { useTrainingSocket } from '../../context/TrainingSocketContext';
import { socketService } from '../../services/trainingSocketService';
import { WebGPUNetworkRendererClass } from '../WebGPUNetworkRendererClass';
import { TrainingProgressData } from '../../services/trainingSocketService';
import './NetworkVisualization3DComponent.css';

interface NetworkVisualization3DComponentProps {
  modelStructure?: any;
  trainingProgressData?: TrainingProgressData[];
  compactView?: boolean;
  onRendererReady?: (renderer: WebGPUNetworkRendererClass | null) => void;
}

/**
 * NetworkVisualization3DComponent
 *
 * Renders a 3D visualization of the neural network being trained using WebGPU.
 * This is a drop-in replacement for NetworkVisualization2DComponent using modern GPU rendering.
 *
 * Features:
 * - 3D perspective view of the neural network
 * - Mouse rotation controls (drag to rotate)
 * - Scroll wheel zoom
 * - Real-time animation of activations and gradients
 * - Orange coloring for forward pass, light blue for backpropagation
 */
const NetworkVisualization3DComponent: React.FC<
  NetworkVisualization3DComponentProps
> = ({ modelStructure, trainingProgressData = [], compactView = false, onRendererReady }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<WebGPUNetworkRendererClass | null>(null);
  const hasRequestedStructure = useRef(false);
  const { modelStructure: contextModelStructure } = useTrainingSocket();
  const [isPaused, setIsPaused] = useState(false);
  const [layerDelay, setLayerDelay] = useState(0);
  const [batchProgressData, setBatchProgressData] = useState<any[]>([]);
  const [selectedLayer, setSelectedLayer] = useState<string>('');
  const [availableLayers, setAvailableLayers] = useState<string[]>([]);
  const currentJobIdRef = useRef<string | null>(null);

  // Request model structure from backend when component mounts
  useEffect(() => {
    if (!hasRequestedStructure.current) {
      hasRequestedStructure.current = true;
      console.log(
        '[NetworkViz 3D] Component mounted, requesting model structure from backend'
      );
      // Request the model structure from the backend
      socketService.emit('requestModelStructure');
      console.log('[NetworkViz 3D] Emitted requestModelStructure event');
    }
  }, []);

  // Capture job_id from modelStructure events
  useEffect(() => {
    const handleModelStructure = (data: any) => {
      if (data.job_id) {
        currentJobIdRef.current = data.job_id;
      }
    };
    
    socketService.on('modelStructure', handleModelStructure);
    
    return () => {
      socketService.off('modelStructure', handleModelStructure);
    };
  }, []);

  // Signal WebGPU visualization opened/closed for backpressure control
  useEffect(() => {
    // Emit opened event when component mounts (with job_id if available)
    socketService.emit('webgpuVisualizationOpened', {
      job_id: currentJobIdRef.current
    });
    
    return () => {
      // Emit closed event when component unmounts (with job_id if available)
      socketService.emit('webgpuVisualizationClosed', {
        job_id: currentJobIdRef.current
      });
    };
  }, []);

  // Track if structure has been set to avoid recreating nodes
  const structureSetRef = useRef(false);
  const structureHashRef = useRef<string>('');
  const lastSetNetworkStructureTimeRef = useRef<number>(0); // Track last call time to prevent rapid succession calls

  // Handle window resize (separate effect, runs once on mount)
  useEffect(() => {
    const handleResize = () => {
      if (rendererRef.current) {
        try {
          rendererRef.current.handleResize();
        } catch (error) {
          console.error('[NetworkViz 3D] Error in handleResize:', error);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    // Initial sizing after a brief delay to ensure initialization
    const resizeTimer = setTimeout(() => {
      handleResize();
    }, 200);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  // Track structure in ref to avoid dependency issues
  const structureRef = useRef<any>(null);
  const initAttemptedRef = useRef(false); // Track if initialization has EVER been attempted

  // Update structure ref when structure becomes available
  useEffect(() => {
    const structure = contextModelStructure || modelStructure;
    if (structure) {
      structureRef.current = structure;
    }
  }, [contextModelStructure, modelStructure]);

  // Initialize renderer ONCE when structure first becomes available
  // This uses a once-only pattern - initAttemptedRef ensures it NEVER runs twice
  useEffect(() => {
    // CRITICAL GUARD: If initialization has EVER been attempted, NEVER run again
    if (initAttemptedRef.current) {
      return; // Silent return - already attempted initialization
    }

    // Get structure from ref (doesn't cause re-renders)
    const structure = structureRef.current;
    if (!structure || !containerRef.current) {
      return; // Not ready yet, wait
    }

    // Mark as attempted IMMEDIATELY to prevent any race conditions
    // This must happen BEFORE any async operations
    initAttemptedRef.current = true;
    structureSetRef.current = true;
    
    console.log('[NetworkViz 3D] INITIALIZING RENDERER - This should only happen ONCE');
    
    const renderer = new WebGPUNetworkRendererClass(containerRef.current);
    rendererRef.current = renderer;
    
    if (onRendererReady) {
      onRendererReady(renderer);
    }

    renderer.waitForInitialization().then(() => {
      if (rendererRef.current && structureSetRef.current) {
        try {
          // Guard: prevent multiple setNetworkStructure calls in rapid succession (< 100ms)
          const now = performance.now();
          const timeSinceLastCall = now - lastSetNetworkStructureTimeRef.current;
          if (timeSinceLastCall < 100) {
            console.log(`[NetworkViz 3D] Skipping setNetworkStructure - called only ${timeSinceLastCall.toFixed(0)}ms ago`);
            return;
          }

          console.log('[NetworkViz 3D] Calling setNetworkStructure...');
          rendererRef.current.setNetworkStructure(structure, compactView);
          lastSetNetworkStructureTimeRef.current = performance.now();
          structureHashRef.current = JSON.stringify(structure); // Store hash for compactView effect
          console.log('[NetworkViz 3D] setNetworkStructure completed - nodes created');
          
          const layers = rendererRef.current.getLayerNames();
          setAvailableLayers(layers);
          if (layers.length > 0 && !selectedLayer) {
            setSelectedLayer('');
          }
        } catch (error) {
          console.error('[NetworkViz 3D] Error setting network structure:', error);
          // Reset flags on error so we can retry
          initAttemptedRef.current = false;
          structureSetRef.current = false;
        }
      }
    }).catch(error => {
      console.error('[NetworkViz 3D] Renderer initialization failed:', error);
      // Reset flags on error so we can retry
      initAttemptedRef.current = false;
      structureSetRef.current = false;
    });
  }, [contextModelStructure, modelStructure]); // Watch for structure arrival, but initAttemptedRef prevents re-runs

  // Track the last structure to avoid recreating nodes unnecessarily
  const lastStructureRef = useRef<any>(null);

  // Track last compactView to avoid unnecessary updates
  const lastCompactViewRef = useRef<boolean | undefined>(undefined);

  // Update structure when compactView changes (but not when structure changes - structure should only be set once)
  useEffect(() => {
    // Don't do anything if structure hasn't been set yet
    if (!structureSetRef.current || !rendererRef.current) {
      return;
    }

    // Only update if compactView actually changed
    if (lastCompactViewRef.current === compactView) {
      return; // compactView hasn't changed, no need to update
    }

    lastCompactViewRef.current = compactView;

    // Use structure from ref (same one used during initialization)
    const structure = structureRef.current;
    if (!structure) {
      return;
    }

    // Only update if structure hash matches what we initialized with
    // This ensures we're not recreating nodes due to structure object reference changes
    const currentHash = JSON.stringify(structure);
    if (structureHashRef.current !== currentHash) {
      console.log('[NetworkViz 3D] Structure hash mismatch in compactView effect - ignoring to prevent node recreation');
      console.log('[NetworkViz 3D] Expected hash:', structureHashRef.current?.substring(0, 50));
      console.log('[NetworkViz 3D] Current hash:', currentHash.substring(0, 50));
      return;
    }
    
    // compactView changed and structure is the same - update renderer
    // The renderer's hash check will prevent node recreation if structure+compactView+viewMode is the same
    console.log('[NetworkViz 3D] compactView changed, updating renderer (renderer will check hash internally)...');
    try {
      // Guard: prevent multiple setNetworkStructure calls in rapid succession (< 100ms)
      const now = performance.now();
      const timeSinceLastCall = now - lastSetNetworkStructureTimeRef.current;
      if (timeSinceLastCall < 100) {
        console.log(`[NetworkViz 3D] Skipping compactView setNetworkStructure - called only ${timeSinceLastCall.toFixed(0)}ms ago`);
        return;
      }

      rendererRef.current.setNetworkStructure(structure, compactView);
      lastSetNetworkStructureTimeRef.current = performance.now();
    } catch (error) {
      console.error('[NetworkViz 3D] Error updating structure for compactView:', error);
    }
  }, [compactView]); // Only depend on compactView, not structure

  // Handle batch progress updates
  useEffect(() => {
    // Use a ref to track if we've already processed this batch to avoid duplicates
    const processedBatches = new Set<string>();
    
    const handleBatchProgress = (data: any) => {
      // Create unique key for this batch to prevent duplicate processing
      const batchKey = `${data.epoch}-${data.batch}`;
      
      // Skip if we've already processed this batch
      if (processedBatches.has(batchKey)) {
        return;
      }
      processedBatches.add(batchKey);
      
      // Track job_id for ack
      if (data.job_id) {
        currentJobIdRef.current = data.job_id;
      }
      
      // Only log occasionally to reduce console spam
      if (data.batch === 1 || data.batch % 100 === 0) {
        console.log(`[NetworkViz 3D] Batch ${data.batch}/${data.total_batches} - Epoch ${data.epoch}`);
      }
      
      // Only keep last 10 batches to prevent memory buildup
      setBatchProgressData((prev) => {
        const updated = [...prev, data];
        return updated.slice(-10); // Keep only last 10 batches
      });
      
      // Update renderer with batch activations
      if (rendererRef.current && data.activations) {
        // Pass activations directly to renderer
        rendererRef.current.updateNodeColorsDirectly(data.activations);
      }
      
      // Send acknowledgment to backend after processing
      if (currentJobIdRef.current && data.epoch !== undefined && data.batch !== undefined) {
        socketService.emit('batchAck', {
          job_id: currentJobIdRef.current,
          epoch: data.epoch,
          batch: data.batch
        });
      }
    };

    const handleTrainingPaused = (data: any) => {
      setIsPaused(true);
    };

    const handleTrainingResumed = (data: any) => {
      setIsPaused(false);
    };

    const handleLayerDelayUpdated = (data: any) => {
      setLayerDelay(data.delay_ms || 0);
    };

    socketService.on('batchProgress', handleBatchProgress);
    socketService.on('trainingPaused', handleTrainingPaused);
    socketService.on('trainingResumed', handleTrainingResumed);
    socketService.on('layerDelayUpdated', handleLayerDelayUpdated);

    return () => {
      socketService.off('batchProgress', handleBatchProgress);
      socketService.off('trainingPaused', handleTrainingPaused);
      socketService.off('trainingResumed', handleTrainingResumed);
      socketService.off('layerDelayUpdated', handleLayerDelayUpdated);
      processedBatches.clear();
    };
  }, []);

  // Update visualization when training progress data changes (epoch-level)
  // Only update when epoch actually changes, not on every batch
  const lastEpochRef = useRef<number>(-1);
  useEffect(() => {
    if (
      rendererRef.current &&
      trainingProgressData &&
      trainingProgressData.length > 0
    ) {
      const latestData = trainingProgressData[trainingProgressData.length - 1];
      const currentEpoch = latestData.epoch;
      
      // Only update if epoch changed (not on every batch)
      if (currentEpoch !== lastEpochRef.current) {
        lastEpochRef.current = currentEpoch;
        
        // Cast to match TrainingData interface expected by renderer
        const trainingDataForRenderer = {
          type: 'progress' as const,
          epoch: latestData.epoch,
          train_loss: latestData.train_loss,
          val_loss: latestData.val_loss,
          activations: (latestData as any).activations || {},
          weights: (latestData as any).weights,
        };
        rendererRef.current.updateTrainingData(trainingDataForRenderer);
      }
    }
  }, [trainingProgressData]);

  // Handle pause/resume button click
  const handlePauseResume = () => {
    if (isPaused) {
      socketService.resumeTraining();
    } else {
      socketService.pauseTraining();
    }
  };

  // Handle delay slider change
  const handleDelayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDelay = parseInt(event.target.value, 10);
    setLayerDelay(newDelay);
    socketService.setLayerDelay(newDelay);
  };

  return (
    <div className="network-visualization-3d-wrapper" style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Control Panel */}
      <div className="training-controls-panel">
        <button
          className={`pause-resume-button ${isPaused ? 'paused' : 'running'}`}
          onClick={handlePauseResume}
          title={isPaused ? 'Resume Training' : 'Pause Training'}
        >
          {isPaused ? '▶ Resume' : '⏸ Pause'}
        </button>
        
        <div className="delay-control">
          <label htmlFor="layer-delay-slider">
            Layer Delay: {layerDelay}ms
          </label>
          <input
            id="layer-delay-slider"
            type="range"
            min="0"
            max="5000"
            step="50"
            value={layerDelay}
            onChange={handleDelayChange}
            className="delay-slider"
          />
        </div>
        
        <div className="layer-select-control">
          <label htmlFor="layer-select">
            Jump to Layer:
          </label>
          <select
            id="layer-select"
            value={selectedLayer}
            onChange={(e) => {
              const layerName = e.target.value;
              setSelectedLayer(layerName);
              if (rendererRef.current) {
                if (layerName === '') {
                  // "All Layers" option - zoom to fit all
                  rendererRef.current.zoomToFitAll();
                } else {
                  // Zoom to specific layer
                  rendererRef.current.zoomToLayer(layerName);
                }
              }
            }}
            className="layer-select"
          >
            <option value="">All Layers</option>
            {availableLayers.map((layerName) => (
              <option key={layerName} value={layerName}>
                {layerName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Visualization Container */}
      <div
        ref={containerRef}
        className="network-visualization-3d-component"
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

// Wrap with React.memo to prevent unnecessary re-renders when parent re-renders
// Only re-render if props actually change
export default memo(NetworkVisualization3DComponent, (prevProps, nextProps) => {
  // Custom comparison: only re-render if structure, compactView, or onRendererReady change
  // trainingProgressData changes are handled by internal useEffect, not by re-rendering
  return (
    prevProps.modelStructure === nextProps.modelStructure &&
    prevProps.compactView === nextProps.compactView &&
    prevProps.onRendererReady === nextProps.onRendererReady
    // Explicitly ignore trainingProgressData - it's handled by internal state
  );
});
